import { type Post, type TopicSection } from '@prisma/client'
import {
	json,
	useLoaderData,
	useRouteLoaderData,
	type MetaFunction,
} from '@remix-run/react'
import { type LoaderFunctionArgs } from '@remix-run/server-runtime'
import ReactMarkdown from 'react-markdown'
import rehypeExternalLinks from 'rehype-external-links'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'
import remarkGfm from 'remark-gfm'
import GlossaryTerms from '#app/components/commonElements/GlossaryTerms.tsx'
import { TableOfContents } from '#app/components/commonElements/TableOfContents.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import PageTitle from '#app/components/pageLayout/PageTitle.tsx'
import ProcedureFAQs from '#app/components/procedures/ProcedureFAQs.tsx'
import { Breadcrumb } from '#app/components/tramite/breadcrumb.tsx'
import { createFAQSchemaFromData, type FAQData } from '#app/utils/faq.ts'
import { invariantResponse } from '#app/utils/misc.tsx'
import { extractHeadingsFromMarkdown } from '#app/utils/toc.ts'
import { findPostBySlug } from '../../_appAdmin+/posts/_postQueries.server'

export async function loader({ params }: LoaderFunctionArgs) {
	const post: Partial<Post> & {
		section: Partial<TopicSection>
	} = await findPostBySlug(params.post!, params.topicSection!, {
		id: true,
		name: true,
		content: true,
		section: true,
		metadata: true,
	})
	invariantResponse(post, 'Contenido no encontrado', { status: 404 })

	return json({
		post,
	})
}

export default function PostRoute() {
	const { topic } = useRouteLoaderData(
		'routes/_publicWithSidebar+/$country+/temas.$topic',
	)
	const { post } = useLoaderData<typeof loader>()
	const section = post.section
	const baseTopicPath = `/${topic.country.code}/temas/`
	const pages = [
		{ name: 'Temas', href: `/${topic.country.code}/temas`, current: false },
		{
			name: topic.name,
			href: `${baseTopicPath}${topic.slug}`,
			ellipsis: true,
			current: false,
		},
		{
			name: section.name,
			href: `${baseTopicPath}${topic.slug}/${section.slug}`,
			current: false,
		},
		{
			name: post.name,
			href: `#`,
			current: true,
		},
	].filter(Boolean)

	const toc = extractHeadingsFromMarkdown(post.content)
	const metadata = JSON.parse((post?.metadata as string) || '{}')
	const faqs: FAQData[] = JSON.parse(metadata?.faqs || '[]')
	const { faqsExist, faqSchema } = createFAQSchemaFromData(faqs)
	const glossary = JSON.parse(metadata?.glossary || '[]')
	return (
		<>
			<div className="mx-auto w-full min-w-0 max-w-4xl px-8 pt-4">
				<Breadcrumb pages={pages} />
				<div className="mt-6">
					<PageTitle
						title={post.name}
						sizeClasses="text-3xl sm:text-2xl lg:text-3xl"
					/>
				</div>
				<div className="prose-a:text-brand-dark prose relative mb-16 max-w-prose prose-a:font-semibold hover:prose-a:text-black">
					<ReactMarkdown
						rehypePlugins={[
							rehypeSlug,
							rehypeRaw,
							remarkGfm,
							[
								rehypeExternalLinks,
								{ target: '_blank', rel: 'noopener noreferrer' },
							],
						]}
						className="prose mt-6 max-w-prose prose-a:font-semibold prose-a:text-amber-700 prose-a:no-underline hover:prose-a:text-amber-400"
						components={{
							a: ({ href, children }) => {
								const isExternal = href?.startsWith('http')
								return (
									<a
										href={href}
										target={isExternal ? '_blank' : undefined}
										rel={isExternal ? 'noopener noreferrer' : undefined}
									>
										{children}
									</a>
								)
							},
						}}
					>
						{post.content}
					</ReactMarkdown>
					{glossary.length > 0 && <GlossaryTerms terms={glossary} />}
					{faqsExist && <ProcedureFAQs faqData={faqs} faqSchema={faqSchema} />}
				</div>
			</div>
			<div className="page-nav sticky top-[6.5rem] -ml-10 mr-10 hidden max-h-[calc(100vh-theme('spacing.36'))] w-64 flex-none self-start text-sm 2xl:block">
				<TableOfContents
					headings={toc}
					otherSections={[
						glossary.length > 0 && {
							id: 'glosario',
							text: 'Glosario',
							heading: 'h2',
						},
						faqsExist && {
							id: 'preguntas-frecuentes',
							text: 'Preguntas frecuentes',
							heading: 'h2',
						},
					]}
				/>
			</div>{' '}
		</>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const post = data?.post.name ?? 'Post'
	const metadata = JSON.parse((post?.metadata as string) || '{}')
	const structuredContent = JSON.parse(metadata?.structured_content || '{}')

	const postSummary = data
		? structuredContent?.meta_description
		: 'No hay contenido'
	const metaTitle = `${post} | Papelea`

	const baseMetaTags = [
		{ title: metaTitle },
		{
			tagName: 'link',
			rel: 'canonical',
			href: `https://papelea.com/${params.country}/${params.topic}/${params.topicSection}/${params.post}`,
		},
		{
			name: 'description',
			content: postSummary,
		},
		{
			name: 'keywords',
			content: metaTitle + ', trámite, administraciones',
		},
		{
			property: 'robots',
			content: 'index, follow',
		},
		{
			property: 'og:type',
			content: 'website',
		},
		{
			property: 'og:url',
			content: `https://papelea.com/${params.country}/${params.topic}/${params.topicSection}/${params.post}`,
		},
		{
			property: 'og:title',
			content: metaTitle,
		},
		{
			name: 'og:description',
			content: postSummary,
		},
	]

	return baseMetaTags
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => <p>No tienes permiso para hacer eso</p>,
				404: ({ params }) => <p>Este contenido no existe en Papelea</p>,
			}}
		/>
	)
}
